﻿import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import type { Receipt as ApiReceipt } from "randmar-api-client";
import { useGetV4WarehousesQuery } from "randmar-api-client";
import { Info } from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import type { Receipt as LegacyReceipt } from "../../stores/document/Document";
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt from "../util/DateTimeUtil";
import { TablePaginationBar } from "../util/TablePaginationBar";

interface IProps {
  receipts?: Array<LegacyReceipt | ApiReceipt>;
  defaultRowsPerPage?: number;
}

interface ReceiptSummaryRow {
  receiptNumber: string;
  purchaseOrderNumber?: string;
  location?: string | null;
  receiptDate?: number;
  invoiced?: boolean;
  invoiceNumber?: string;
  comments?: string;
  subTotal: number;
  taxAmount1: number;
  taxRate1: number;
  taxAmount2: number;
  taxRate2: number;
  total: number;
}

const rowsPerPageOptions = [10, 20, 50, 100] as const;

const isLegacyReceipt = (receipt: LegacyReceipt | ApiReceipt): receipt is LegacyReceipt =>
  "receiptNumber" in receipt;

type ReceiptFinancials = {
  SubTotal?: number | null;
  TaxAmount1?: number | null;
  TaxRate1?: number | null;
  TaxAmount2?: number | null;
  TaxRate2?: number | null;
  Total?: number | null;
};

const getApiReceiptFinancials = (receipt: ApiReceipt): ReceiptFinancials | null => {
  const record = receipt as unknown as Record<string, unknown>;
  const subTotal = typeof record.SubTotal === "number" ? record.SubTotal : null;
  const taxAmount1 = typeof record.TaxAmount1 === "number" ? record.TaxAmount1 : null;
  const taxRate1 = typeof record.TaxRate1 === "number" ? record.TaxRate1 : null;
  const taxAmount2 = typeof record.TaxAmount2 === "number" ? record.TaxAmount2 : null;
  const taxRate2 = typeof record.TaxRate2 === "number" ? record.TaxRate2 : null;
  const total = typeof record.Total === "number" ? record.Total : null;

  if (
    subTotal === null &&
    taxAmount1 === null &&
    taxRate1 === null &&
    taxAmount2 === null &&
    taxRate2 === null &&
    total === null
  ) {
    return null;
  }

  return {
    SubTotal: subTotal,
    TaxAmount1: taxAmount1,
    TaxRate1: taxRate1,
    TaxAmount2: taxAmount2,
    TaxRate2: taxRate2,
    Total: total,
  };
};

const pickRepresentativeApiReceipt = (lines: ApiReceipt[]): ApiReceipt => {
  return lines.reduce((best, current) => {
    const bestFinancials = getApiReceiptFinancials(best);
    const currentFinancials = getApiReceiptFinancials(current);
    const bestScore = Math.abs(
      bestFinancials?.Total ??
        bestFinancials?.SubTotal ??
        (best.Quantity ?? 0) * (best.UnitPrice ?? 0)
    );
    const currentScore = Math.abs(
      currentFinancials?.Total ??
        currentFinancials?.SubTotal ??
        (current.Quantity ?? 0) * (current.UnitPrice ?? 0)
    );
    return currentScore > bestScore ? current : best;
  }, lines[0]);
};

const deriveTaxRate = (taxAmount: number, subTotal: number): number => {
  if (!subTotal) return 0;
  return Math.round(((taxAmount / subTotal) * 100) * 100) / 100;
};

const ReceiptsGrid: React.FC<IProps> = ({ receipts, defaultRowsPerPage = 20 }) => {
  const { applicationId } = useManufacturer();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const { data: warehouses } = useGetV4WarehousesQuery();

  const getWarehouseNameFromCode = useCallback((code: string | undefined | null): string => {
    if (!code || !warehouses) {
      return "B/O";
    }
    const warehouse = warehouses.find(w => w.WarehouseId === code);
    return warehouse?.Name || "B/O";
  }, [warehouses]);

  const summarizedRows = useMemo<ReceiptSummaryRow[]>(() => {
    const legacy = (receipts ?? []).filter(isLegacyReceipt);
    const api = (receipts ?? []).filter((value): value is ApiReceipt => !isLegacyReceipt(value));

    const legacySummaries: ReceiptSummaryRow[] = legacy
      .filter((receipt) => Boolean(receipt.receiptNumber))
      .map((receipt) => {
        const subTotal = receipt.subTotal ?? 0;
        const taxAmount1 = receipt.taxAmount1 ?? 0;
        const taxAmount2 = receipt.taxAmount2 ?? 0;
        const total = receipt.total ?? subTotal + taxAmount1 + taxAmount2;

        return {
          receiptNumber: receipt.receiptNumber || "-",
          purchaseOrderNumber: receipt.purchaseOrderNumber || undefined,
          location: receipt.location,
          receiptDate: receipt.receiptDate,
          invoiced: receipt.invoiced,
          invoiceNumber: receipt.invoiceNumber || undefined,
          comments: receipt.comments || undefined,
          subTotal,
          taxAmount1,
          taxRate1: taxAmount1 ? deriveTaxRate(taxAmount1, subTotal) : 0,
          taxAmount2,
          taxRate2: taxAmount2 ? deriveTaxRate(taxAmount2, subTotal) : 0,
          total,
        };
      });

    const apiGrouped = new Map<string, ApiReceipt[]>();
    for (const line of api) {
      const receiptNumber = line.ReceiptNumber ?? "";
      if (!receiptNumber) continue;
      const existing = apiGrouped.get(receiptNumber);
      if (!existing) {
        apiGrouped.set(receiptNumber, [line]);
      } else {
        existing.push(line);
      }
    }

    const apiSummaries: ReceiptSummaryRow[] = Array.from(apiGrouped.values()).map((lines) => {
      const representative = pickRepresentativeApiReceipt(lines);
      const financials = getApiReceiptFinancials(representative);
      const linesSubTotal = lines.reduce(
        (sum, line) => sum + (line.Quantity ?? 0) * (line.UnitPrice ?? 0),
        0
      );

      const subTotal = financials?.SubTotal ?? linesSubTotal;

      const taxRate1 =
        financials?.TaxRate1 ??
        (typeof financials?.TaxAmount1 === "number" ? deriveTaxRate(financials.TaxAmount1, subTotal) : 0);
      const taxRate2 =
        financials?.TaxRate2 ??
        (typeof financials?.TaxAmount2 === "number" ? deriveTaxRate(financials.TaxAmount2, subTotal) : 0);

      const taxAmount1 =
        financials?.TaxAmount1 ?? (taxRate1 ? (subTotal * taxRate1) / 100 : 0);
      const taxAmount2 =
        financials?.TaxAmount2 ?? (taxRate2 ? (subTotal * taxRate2) / 100 : 0);

      const total =
        financials?.Total ??
        (subTotal || taxAmount1 || taxAmount2 ? subTotal + taxAmount1 + taxAmount2 : 0);

      const receiptDate = lines.reduce(
        (best, line) => Math.max(best, line.ReceiptDate ?? 0),
        0
      ) || undefined;
      const location = lines.map((line) => line.Location).find((value) => Boolean(value)) ?? representative.Location;
      const purchaseOrderNumber =
        lines
          .map((line) => {
            const record = line as unknown as Record<string, unknown>;
            if (typeof record.PurchaseOrderNumber === "string") return record.PurchaseOrderNumber;
            if (typeof record.purchaseOrderNumber === "string") return record.purchaseOrderNumber;
            return null;
          })
          .find((value): value is string => Boolean(value)) ?? undefined;

      return {
        receiptNumber: representative.ReceiptNumber ?? "-",
        purchaseOrderNumber,
        location,
        receiptDate,
        invoiced: undefined,
        invoiceNumber: undefined,
        comments: undefined,
        subTotal,
        taxAmount1,
        taxRate1,
        taxAmount2,
        taxRate2,
        total,
      };
    });

    return [...legacySummaries, ...apiSummaries].sort(
      (a, b) =>
        (b.receiptDate ?? 0) - (a.receiptDate ?? 0) ||
        b.receiptNumber.localeCompare(a.receiptNumber)
    );
  }, [receipts]);

  if (!summarizedRows.length) {
    return (
      <Alert className="w-full rounded-2xl border border-gray-100 bg-white shadow-sm">
        <Info className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>Receipts</AlertTitle>
        <AlertDescription>
          Receipts document inventory received into Randmar warehouses. There are no receipts to display right now.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <section className="w-full">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-[20px] font-medium text-slate-900">Receipts</h3>
          <p className="text-[15px] text-slate-600">
            Totals are aggregated across receipt line items.
          </p>
        </div>
        <p className="text-sm text-slate-500">
          {summarizedRows.length} receipt{summarizedRows.length === 1 ? "" : "s"}
        </p>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-gray-100">
        <Table>
          <TableHeader className="bg-gray-50">
            <TableRow className="h-14 hover:bg-gray-50">
              <TableHead className="min-w-[170px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                Receipt
              </TableHead>
              <TableHead className="min-w-[170px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                PO number
              </TableHead>
              <TableHead className="min-w-[180px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                Location
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Date
              </TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Subtotal
              </TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Tax 1
              </TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Tax 2
              </TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Total
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summarizedRows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => (
                <TableRow
                  key={row.receiptNumber}
                  className="h-14 border-b border-gray-100 hover:bg-gray-50/60"
                >
                  <TableCell className="font-semibold">
                    <Link
                      to={Path.document(applicationId, row.receiptNumber)}
                      className="text-slate-900 underline-offset-4 hover:text-[color:var(--ops-accent)] hover:underline"
                    >
                      {row.receiptNumber}
                    </Link>
                  </TableCell>
                  <TableCell className="text-slate-700">{row.purchaseOrderNumber || "-"}</TableCell>
                  <TableCell className="text-slate-700">
                    {getWarehouseNameFromCode(row.location)}
                  </TableCell>
                  <TableCell className="text-slate-700">{FormatDateFromInt(row.receiptDate)}</TableCell>
                  <TableCell className="text-right font-medium text-slate-900">
                    <FormattedNumber {...CurrencyUtils.format} value={row.subTotal} />
                  </TableCell>
                  <TableCell className="text-right text-slate-700">
                    {row.taxAmount1 || row.taxRate1 ? (
                      <span className="whitespace-nowrap">
                        <FormattedNumber {...CurrencyUtils.format} value={row.taxAmount1} />
                        {row.taxRate1 ? (
                          <span className="ml-1 text-xs text-slate-500">({row.taxRate1}%)</span>
                        ) : null}
                      </span>
                    ) : (
                      <span className="text-slate-500">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-slate-700">
                    {row.taxAmount2 || row.taxRate2 ? (
                      <span className="whitespace-nowrap">
                        <FormattedNumber {...CurrencyUtils.format} value={row.taxAmount2} />
                        {row.taxRate2 ? (
                          <span className="ml-1 text-xs text-slate-500">({row.taxRate2}%)</span>
                        ) : null}
                      </span>
                    ) : (
                      <span className="text-slate-500">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-medium text-slate-900">
                    <FormattedNumber {...CurrencyUtils.format} value={row.total} />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {summarizedRows.length > rowsPerPage && (
        <TablePaginationBar
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={summarizedRows.length}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={(nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(nextRows) => setRowsPerPage(nextRows)}
        />
      )}
    </section>
  );
};

export default ReceiptsGrid;


